import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import { authService } from '../services/api';
import Sidebar from '../components/Sidebar';
import TopNav from '../components/TopNav';
import { useTranslation } from 'react-i18next';
import OfflineDownloadsCard from '../components/OfflineDownloadsCard';

function Profile() {
  const navigate = useNavigate();
  const { user, isAuthenticated, updateUser } = useAuthStore();
  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '', date_of_birth: '' });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [previewIsObjectUrl, setPreviewIsObjectUrl] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const mountedRef = useRef(true);
  const { t } = useTranslation();

  useEffect(() => {
    mountedRef.current = true;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // prefill from auth store immediately to avoid empty fields when network is slow
    if (user) {
      setForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        date_of_birth: user.date_of_birth || '',
      });
      setAvatarPreview(user.profile_picture || null);
      setPreviewIsObjectUrl(false);
    }

    (async () => {
      try {
        const resp = await authService.getCurrentUser();
        const data = resp.data || resp;
        if (!mountedRef.current) return;
        setForm({
          first_name: data.first_name || (user?.first_name || ''),
          last_name: data.last_name || (user?.last_name || ''),
          phone: data.phone || (user?.phone || ''),
          date_of_birth: data.date_of_birth || (user?.date_of_birth || ''),
        });
        setAvatarPreview(data.profile_picture || user?.profile_picture || null);
        setPreviewIsObjectUrl(false);
      } catch (e) {
        console.error('Failed to load profile from API, falling back to local auth store', e);
        // keep auth-store values already set
      }
    })();

    return () => {
      mountedRef.current = false;
      if (previewIsObjectUrl && avatarPreview) URL.revokeObjectURL(avatarPreview);
    };
  }, [isAuthenticated, navigate]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleAvatar = (e) => {
    const f = e.target.files && e.target.files[0];
    setErrors(prev => ({ ...prev, profile_picture: undefined }));
    if (!f) {
      setAvatarFile(null);
      setAvatarPreview(user?.profile_picture || null);
      setPreviewIsObjectUrl(false);
      return;
    }
    if (!f.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, profile_picture: 'Please select an image file' }));
      return;
    }
    const max = 5 * 1024 * 1024;
    if (f.size > max) {
      setErrors(prev => ({ ...prev, profile_picture: 'Image must be smaller than 5MB' }));
      return;
    }
    setAvatarFile(f);
    try {
      if (previewIsObjectUrl && avatarPreview) URL.revokeObjectURL(avatarPreview);
      const url = URL.createObjectURL(f);
      setAvatarPreview(url);
      setPreviewIsObjectUrl(true);
    } catch (err) {
      setAvatarPreview(null);
      setPreviewIsObjectUrl(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    try {
      const fd = new FormData();
      Object.keys(form).forEach(k => { if (form[k] !== undefined && form[k] !== null) fd.append(k, form[k]); });
      if (avatarFile) fd.append('profile_picture', avatarFile);
      const res = await authService.updateProfile(fd, (progressEvent) => {
        if (progressEvent.total) {
          const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(pct);
        }
      });
      const updated = res.data || res;
      updateUser(updated);
      setAvatarPreview(updated.profile_picture || null);
      setPreviewIsObjectUrl(false);
      setUploadProgress(0);
      setErrors({ _success: t('Profile updated') });
    } catch (err) {
      console.error('Profile update failed', err);
      if (err.response && err.response.data && typeof err.response.data === 'object') {
        const data = err.response.data;
        // prefer `detail` key if present
        if (data.detail) {
          setErrors({ _error: String(data.detail) });
        } else {
          // field errors: convert arrays to strings
          const converted = {};
          Object.entries(data).forEach(([k, v]) => {
            if (Array.isArray(v)) converted[k] = v.join(' ');
            else converted[k] = String(v);
          });
          setErrors(converted);
        }
      } else {
        setErrors({ _error: t('Failed to update profile') });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title="Profile" subtitle="Manage your account" />
        <main className="flex-1 p-6 overflow-auto">
          <div className="max-w-3xl mx-auto bg-white shadow rounded-lg p-6">
            <h2 className="text-2xl font-semibold mb-4">{t('Profile')}</h2>
              {user?.roles?.some((r) => r.name?.toLowerCase() === 'teacher') && (
                <div className="mb-4 p-3 bg-primary-50 border border-primary-100 rounded-lg text-sm text-primary-800 flex items-center justify-between gap-3 flex-wrap">
                  <span>{t('Looking to update your cover photo, bio, qualifications or specializations? That\'s on your public teacher profile.')}</span>
                  <div className="flex flex-wrap items-center gap-4">
                    <Link to="/teacher/blog" className="font-medium text-primary-700 hover:text-primary-900 underline whitespace-nowrap">
                      {t('Manage my blog')} →
                    </Link>
                    <Link to={`/teachers/${user.id}`} className="font-medium text-primary-700 hover:text-primary-900 underline whitespace-nowrap">
                      {t('Go to my public profile')} →
                    </Link>
                  </div>
                </div>
              )}
              {user?.roles?.some((r) => r.name?.toLowerCase() === 'administrator') && (
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-100 bg-emerald-50 p-3 text-sm text-emerald-800">
                  <span>{t('Review infrastructure health, service availability and application performance.')}</span>
                  <Link to="/admin/monitoring" className="whitespace-nowrap font-medium text-emerald-700 underline hover:text-emerald-900">
                    {t('Open system monitoring')} →
                  </Link>
                </div>
              )}
              {errors._success && <div className="mb-3 p-2 bg-green-50 text-green-700 rounded">{errors._success}</div>}
              {errors._error && <div className="mb-3 p-2 bg-red-50 text-red-700 rounded">{errors._error}</div>}
            <form onSubmit={handleSubmit} encType="multipart/form-data">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('First name')}</label>
                  <input name="first_name" value={form.first_name} onChange={handleChange} className="mt-1 block w-full border rounded px-3 py-2" />
                  {errors.first_name && <div className="text-sm text-red-600 mt-1">{errors.first_name}</div>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('Last name')}</label>
                  <input name="last_name" value={form.last_name} onChange={handleChange} className="mt-1 block w-full border rounded px-3 py-2" />
                  {errors.last_name && <div className="text-sm text-red-600 mt-1">{errors.last_name}</div>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('Phone')}</label>
                  <input name="phone" value={form.phone} onChange={handleChange} className="mt-1 block w-full border rounded px-3 py-2" />
                  {errors.phone && <div className="text-sm text-red-600 mt-1">{errors.phone}</div>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('Date of birth')}</label>
                  <input name="date_of_birth" type="date" value={form.date_of_birth} onChange={handleChange} className="mt-1 block w-full border rounded px-3 py-2" />
                  {errors.date_of_birth && <div className="text-sm text-red-600 mt-1">{errors.date_of_birth}</div>}
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700">{t('Avatar')}</label>
                  <div className="mt-1 flex items-center space-x-4">
                    <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-200">
                      {avatarPreview ? (
                        <img src={avatarPreview} alt="avatar preview" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-3xl text-gray-500">{(user?.first_name||'')[0]}{(user?.last_name||'')[0]}</div>
                      )}
                    </div>
                    <div className="flex-1">
                      <input name="profile_picture" type="file" accept="image/*" onChange={handleAvatar} className="mt-1" />
                      {errors.profile_picture && <div className="text-sm text-red-600 mt-1">{errors.profile_picture}</div>}
                      {uploadProgress > 0 && (
                        <div className="w-full bg-gray-200 rounded mt-2 h-2">
                          <div className="bg-primary-600 text-white text-xs rounded h-2" style={{ width: `${uploadProgress}%` }} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

                <div className="mt-6 flex items-center justify-end space-x-3">
                <button type="button" onClick={() => navigate('/dashboard')} className="px-4 py-2 border rounded">{t('Cancel')}</button>
                <button type="submit" disabled={loading} className="px-4 py-2 bg-primary-600 text-white rounded">{loading ? t('Saving...') : t('Save Profile')}</button>
              </div>
            </form>

            <OfflineDownloadsCard />
          </div>
        </main>
      </div>
    </div>
  );
}

export default Profile;
